import { createTheme, Theme } from '@mui/material/styles';
import { breakpoints } from './foundations/breakpoints';
import {
  signalBorders,
  signalBordersLight,
  signalColors,
  signalColorsOnLight,
  signalFonts,
  signalRadii,
  signalSurfaces,
  signalSurfacesLight,
  signalText,
  signalTextLight,
  tokens,
} from './foundations/tokens';

/**
 * MUI theme for the Signal System surfaces.
 *
 * Deliberately separate from the app's `AppThemeProvider` so the redesign can
 * be developed and reviewed without touching the theme every existing page
 * renders through. The screens themselves style with the raw tokens; this
 * theme exists so any MUI component dropped inside a Signal screen inherits
 * the right palette and typefaces instead of the default blue/Roboto.
 */
export const createSignalTheme = (mode: 'light' | 'dark' = 'dark'): Theme => {
  const isDark = mode === 'dark';

  return createTheme({
    breakpoints,
    palette: {
      studio: tokens(mode),
      mode,
      primary: {
        main: isDark ? signalColors.accent : signalColorsOnLight.accent,
        light: signalColors.accentBright,
        dark: signalColorsOnLight.accentGradientStart,
        contrastText: signalColors.onAccent,
      },
      secondary: {
        main: isDark ? signalColors.cyan : signalColorsOnLight.cyan,
        light: signalColors.cyanLight,
        dark: signalColorsOnLight.cyan,
        contrastText: signalColors.onCyan,
      },
      success: {
        main: isDark ? signalColors.green : signalColorsOnLight.green,
        contrastText: signalColors.onGreen,
      },
      warning: {
        main: signalColors.amber,
        contrastText: signalColors.onAccent,
      },
      error: { main: isDark ? signalColors.red : signalColorsOnLight.red },
      info: { main: isDark ? signalColors.cyan : signalColorsOnLight.cyan },
      background: {
        default: isDark ? signalSurfaces.canvas : '#F4F4F2',
        paper: isDark ? signalSurfaces.panel : signalSurfacesLight.glassDrawer,
      },
      text: {
        primary: isDark ? signalText.primary : signalTextLight.primary,
        secondary: isDark ? signalText.secondary : signalTextLight.secondary,
        disabled: isDark ? signalText.faint : signalTextLight.faint,
      },
      divider: isDark ? signalBorders.divider : signalBordersLight.divider,
    },
    shape: { borderRadius: signalRadii.md },
    typography: {
      fontFamily: signalFonts.ui,
      // Display face carries every heading; UI face carries everything else.
      h1: {
        fontFamily: signalFonts.display,
        fontWeight: 800,
        fontSize: 76,
        lineHeight: 0.92,
        letterSpacing: '-0.04em',
      },
      h2: {
        fontFamily: signalFonts.display,
        fontWeight: 800,
        fontSize: 46,
        lineHeight: 1,
        letterSpacing: '-0.035em',
      },
      h3: {
        fontFamily: signalFonts.display,
        fontWeight: 800,
        fontSize: 34,
        lineHeight: 1,
        letterSpacing: '-0.03em',
      },
      h4: {
        fontFamily: signalFonts.display,
        fontWeight: 800,
        fontSize: 30,
        lineHeight: 1,
        letterSpacing: '-0.03em',
      },
      h5: {
        fontFamily: signalFonts.display,
        fontWeight: 700,
        fontSize: 24,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      },
      h6: {
        fontFamily: signalFonts.display,
        fontWeight: 700,
        fontSize: 18,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      body1: { fontSize: 14, lineHeight: 1.6 },
      body2: { fontSize: 13, lineHeight: 1.6 },
      caption: { fontSize: 12, lineHeight: 1.5 },
      button: { fontWeight: 600, fontSize: 13, textTransform: 'none' },
      overline: {
        fontFamily: signalFonts.mono,
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? signalSurfaces.canvas : '#F4F4F2',
            WebkitFontSmoothing: 'antialiased',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: signalRadii.md, padding: '9px 16px' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${
              isDark ? signalBorders.medium : signalBordersLight.strong
            }`,
          },
        },
      },
    },
  });
};

export const signalTheme = createSignalTheme('dark');
